import React, { useEffect, useRef, useState, useCallback } from'react';
import { Link } from'react-router-dom';
import { gsap } from'../../animations/gsap.config';
import { ScrollTrigger } from'../../animations/gsap.config';
import homeData from'../../data/home.json';
import {
 Search,
 BarChart3,
 Shield,
 GraduationCap,
 ArrowRight,
 ArrowUpRight,
 CheckCircle,
 ChevronRight,
 Sparkles,
 MoveRight,
} from'lucide-react';

/* ─────────────────────────────────────────────
 ICON MAP
───────────────────────────────────────────── */
const ICON_MAP = {
 Search,
 BarChart3,
 Shield,
 GraduationCap,
};

/* ─────────────────────────────────────────────
 MAGNETIC BUTTON HOOK
───────────────────────────────────────────── */
const useMagnetic = (strength = 0.4) => {
 const ref = useRef(null);

 useEffect(() => {
 const el = ref.current;
 if (!el) return;

 const handleMove = (e) => {
 const rect = el.getBoundingClientRect();
 const cx = rect.left + rect.width / 2;
 const cy = rect.top + rect.height / 2;
 const dx = e.clientX - cx;
 const dy = e.clientY - cy;

 gsap.to(el, {
 x: dx * strength,
 y: dy * strength,
 duration: 0.4,
 ease:'power2.out',
 });
 };

 const handleLeave = () => {
 gsap.to(el, {
 x: 0, y: 0,
 duration: 0.6,
 ease:'elastic.out(1, 0.4)',
 });
 };

 el.addEventListener('mousemove', handleMove);
 el.addEventListener('mouseleave', handleLeave);
 return () => {
 el.removeEventListener('mousemove', handleMove);
 el.removeEventListener('mouseleave', handleLeave);
 };
 }, [strength]);

 return ref;
};

/* ─────────────────────────────────────────────
 3D TILT HOOK
───────────────────────────────────────────── */
const useTilt = (maxTilt = 8) => {
 const ref = useRef(null);

 useEffect(() => {
 const el = ref.current;
 if (!el) return;

 const handleMove = (e) => {
 const rect = el.getBoundingClientRect();
 const x = (e.clientX - rect.left) / rect.width - 0.5;
 const y = (e.clientY - rect.top) / rect.height - 0.5;

 gsap.to(el, {
 rotateY: x * maxTilt,
 rotateX: -y * maxTilt,
 duration: 0.5,
 ease:'power2.out',
 transformPerspective: 800,
 transformOrigin:'center center',
 });
 };

 const handleLeave = () => {
 gsap.to(el, {
 rotateY: 0,
 rotateX: 0,
 duration: 0.7,
 ease:'elastic.out(1, 0.4)',
 });
 };

 el.addEventListener('mousemove', handleMove);
 el.addEventListener('mouseleave', handleLeave);

 return () => {
 el.removeEventListener('mousemove', handleMove);
 el.removeEventListener('mouseleave', handleLeave);
 };
 }, [maxTilt]);

 return ref;
};

/* ─────────────────────────────────────────────
 SERVICE NUMBER (animated stroke)
───────────────────────────────────────────── */
const ServiceNumber = ({ number, color, isHovered }) => {
 const ref = useRef(null);

 useEffect(() => {
 if (!ref.current) return;
 gsap.to(ref.current, {
 opacity: isHovered ? 1 : 0.15,
 scale: isHovered ? 1.1 : 1,
 duration: 0.4,
 ease:'power2.out',
 });
 }, [isHovered]);

 return (
 <div
 ref={ref}
 className="absolute top-4 right-5 font-black font-mono
 leading-none select-none pointer-events-none transition-none"
 style={{
 fontSize:'5rem',
 color,
 opacity: 0.15,
 WebkitTextStroke:`1px ${color}`,
 WebkitTextFillColor:'transparent',
 }}
 >
 {number}
 </div>
 );
};

/* ─────────────────────────────────────────────
 FEATURE LIST ITEM
───────────────────────────────────────────── */
const FeatureItem = ({ text, color, delay }) => {
 const ref = useRef(null);

 return (
 <div
 ref={ref}
 className="feature-item flex items-center gap-2.5 text-sm text-slate-600"
 >
 <div
 className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
 style={{ background:`${color}20` }}
 >
 <CheckCircle size={10} style={{ color }} />
 </div>
 {text}
 </div>
 );
};

/* ─────────────────────────────────────────────
 PROGRESS BAR (inside card on hover)
───────────────────────────────────────────── */
const CardProgressBar = ({ color, isHovered }) => {
 const ref = useRef(null);

 useEffect(() => {
 if (!ref.current) return;
 gsap.to(ref.current, {
 scaleX: isHovered ? 1 : 0,
 duration: isHovered ? 0.6 : 0.3,
 ease: isHovered ?'power3.out' :'power2.in',
 transformOrigin:'left center',
 });
 }, [isHovered]);

 return (
 <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden rounded-b-2xl">
 <div
 ref={ref}
 className="h-full rounded-full origin-left"
 style={{
 background:`linear-gradient(90deg, ${color}, transparent)`,
 transform:'scaleX(0)',
 }}
 />
 </div>
 );
};

/* ─────────────────────────────────────────────
 SERVICE CARD
───────────────────────────────────────────── */
const ServiceCard = ({ service, index }) => {
 const [isHovered, setIsHovered] = useState(false);
 const tiltRef = useTilt(6);
 const cardRef = useRef(null);
 const iconRef = useRef(null);
 const featRef = useRef(null);
 const arrowRef = useRef(null);
 const glowRef = useRef(null);
 const IconC = ICON_MAP[service.icon] || Search;

 /* Combine tilt + card refs */
 const setRefs = useCallback((el) => {
 cardRef.current = el;
 tiltRef.current = el;
 }, [tiltRef]);

 /* Hover enter */
 const handleEnter = useCallback(() => {
 setIsHovered(true);

 /* Icon spin + scale */
 gsap.to(iconRef.current, {
 rotate: 360,
 scale: 1.15,
 duration: 0.6,
 ease:'back.out(1.7)',
 });

 /* Glow */
 gsap.to(glowRef.current, {
 opacity: 1,
 scale: 1,
 duration: 0.5,
 ease:'power2.out',
 });

 /* Feature items stagger in */
 gsap.fromTo(
 featRef.current?.querySelectorAll('.feature-item') ?? [],
 { x: -10, opacity: 0 },
 {
 x: 0,
 opacity: 1,
 stagger: 0.08,
 duration: 0.4,
 ease:'power3.out',
 },
 );

 /* Arrow slide */
 gsap.to(arrowRef.current, {
 x: 4,
 opacity: 1,
 duration: 0.3,
 ease:'power2.out',
 });
 }, []);

 /* Hover leave */
 const handleLeave = useCallback(() => {
 setIsHovered(false);

 gsap.to(iconRef.current, {
 rotate: 0,
 scale: 1,
 duration: 0.5,
 ease:'power2.out',
 });

 gsap.to(glowRef.current, {
 opacity: 0,
 scale: 0.8,
 duration: 0.4,
 ease:'power2.in',
 });

 gsap.to(arrowRef.current, {
 x: 0,
 opacity: 0.5,
 duration: 0.3,
 });
 }, []);

 return (
 <div
 ref={setRefs}
 onMouseEnter={handleEnter}
 onMouseLeave={handleLeave}
 data-cursor="card"
 className="service-card-wrap relative rounded-2xl overflow-hidden
 cursor-pointer will-change-transform bg-white shadow-lg border border-slate-200"
 style={{
 backdropFilter:'blur(16px)',
 transformStyle:'preserve-3d',
 }}
 >
 {/* Glow overlay */}
 <div
 ref={glowRef}
 className="absolute inset-0 pointer-events-none rounded-2xl"
 style={{
 background:`radial-gradient(ellipse 80% 60% at 50% 0%, ${service.color}08 0%, transparent 60%)`,
 opacity: 0,
 transform:'scale(0.8)',
 }}
 />

 {/* Gradient bg */}
 <div
 className={`absolute inset-0 bg-gradient-to-br ${service.gradient}
 opacity-0 transition-opacity duration-500
 ${isHovered ?'opacity-100' :''}`}
 />

 {/* Service number */}
 <ServiceNumber
 number={service.number}
 color={service.color}
 isHovered={isHovered}
 />

 {/* Content */}
 <div className="relative z-10 p-7 space-y-5">

 {/* Icon */}
 <div
 ref={iconRef}
 className="relative w-14 h-14 rounded-2xl flex items-center
 justify-center flex-shrink-0"
 style={{
 background:`${service.color}20`,
 border:`1px solid ${service.color}30`,
 }}
 >
 <IconC size={24} style={{ color: service.color }} />
 {/* Icon glow dot */}
 <div
 className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
 style={{ background: service.color, opacity: 0.7 }}
 />
 </div>

 {/* Title + short desc */}
 <div className="space-y-2">
 <h3
 className="font-bold text-slate-900 text-xl leading-snug
 group-hover:text-gradient-primary transition-all duration-300"
 >
 {service.title}
 </h3>
 <p className="text-slate-600 text-sm leading-relaxed">
 {service.shortDesc}
 </p>
 </div>

 {/* Divider */}
 <div
 className="h-px w-full"
 style={{
 background:`linear-gradient(to right, ${service.color}40, transparent)`,
 }}
 />

 {/* Feature list */}
 <div ref={featRef} className="space-y-2.5">
 {service.features.map((feat, i) => (
 <FeatureItem
 key={i}
 text={feat}
 color={service.color}
 delay={i * 0.08}
 />
 ))}
 </div>

 {/* CTA link */}
 <Link
 to={service.href}
 className="inline-flex items-center gap-2 text-sm font-semibold
 transition-colors duration-300 group/link mt-2"
 style={{ color: service.color }}
 onClick={(e) => e.stopPropagation()}
 >
 <span>Learn More</span>
 <div ref={arrowRef} style={{ opacity: 0.5 }}>
 <ArrowRight
 size={14}
 className="transition-transform duration-300
 group-hover/link:translate-x-1"
 />
 </div>
 </Link>
 </div>

 {/* Bottom progress bar */}
 <CardProgressBar color={service.color} isHovered={isHovered} />
 </div>
 );
};

/* ─────────────────────────────────────────────
 ACTIVE SERVICE DETAIL PANEL
 (shown on the right for large screens)
───────────────────────────────────────────── */
const DetailPanel = ({ service }) => {
 const panelRef = useRef(null);
 const prevId = useRef(null);
 const IconC = ICON_MAP[service.icon] || Search;

 useEffect(() => {
 if (!panelRef.current || prevId.current === service.id) return;
 prevId.current = service.id;

 const tl = gsap.timeline();
 tl.fromTo(
 panelRef.current,
 { opacity: 0, y: 20 },
 { opacity: 1, y: 0, duration: 0.5, ease:'power3.out' },
 );
 }, [service]);

 return (
 <div
 ref={panelRef}
 className="sticky top-28 rounded-3xl p-8 space-y-6 overflow-hidden
 bg-white shadow-lg border border-slate-200"
 >
 {/* BG glow */}
 <div
 className="absolute inset-0 pointer-events-none"
 style={{
 background:`radial-gradient(ellipse 70% 50% at 50% 0%,
 ${service.color}08 0%, transparent 60%)`,
 }}
 />

 {/* Number watermark */}
 <div
 className="absolute -bottom-4 -right-4 font-black font-mono
 leading-none select-none pointer-events-none"
 style={{
 fontSize:'8rem',
 color: service.color,
 opacity: 0.06,
 WebkitTextStroke:`2px ${service.color}`,
 WebkitTextFillColor:'transparent',
 }}
 >
 {service.number}
 </div>

 <div className="relative z-10 space-y-6">
 {/* Icon + title */}
 <div className="flex items-start gap-4">
 <div
 className="w-16 h-16 rounded-2xl flex items-center
 justify-center flex-shrink-0"
 style={{
 background:`${service.color}20`,
 border:`1px solid ${service.color}30`,
 }}
 >
 <IconC size={28} style={{ color: service.color }} />
 </div>
 <div>
 <p
 className="text-xs font-semibold tracking-[0.2em] uppercase mb-1"
 style={{ color: service.color }}
 >
 Service {service.number}
 </p>
 <h3 className="text-2xl font-bold text-slate-900 leading-tight">
 {service.title}
 </h3>
 </div>
 </div>

 {/* Divider */}
 <div
 className="h-px w-full"
 style={{
 background:`linear-gradient(to right, ${service.color}50, transparent)`,
 }}
 />

 {/* Full description */}
 <p className="text-slate-700 leading-relaxed text-[15px]">
 {service.description}
 </p>

 {/* Features */}
 <div className="space-y-3">
 <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
 What's Included
 </p>
 {service.features.map((feat, i) => (
 <div key={i} className="flex items-center gap-3">
 <div
 className="w-5 h-5 rounded-full flex items-center
 justify-center flex-shrink-0"
 style={{ background:`${service.color}25` }}
 >
 <CheckCircle size={11} style={{ color: service.color }} />
 </div>
 <span className="text-sm text-slate-700">{feat}</span>
 </div>
 ))}
 </div>

 {/* CTA */}
 <Link
 to={service.href}
 className="group flex items-center justify-between w-full
 px-5 py-4 rounded-xl font-semibold text-sm
 transition-all duration-300 hover:-translate-y-0.5"
 style={{
 background:`${service.color}20`,
 border:`1px solid ${service.color}30`,
 color: service.color,
 }}
 >
 <span>View Full Details</span>
 <ArrowUpRight
 size={16}
 className="transition-transform duration-300
 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
 />
 </Link>
 </div>
 </div>
 );
};

/* ─────────────────────────────────────────────
 SECTION HEADER
───────────────────────────────────────────── */
const SectionHeader = ({ data }) => {
 const labelRef = useRef(null);
 const headRef = useRef(null);
 const subRef = useRef(null);
 const lineRef = useRef(null);

 useEffect(() => {
 const ctx = gsap.context(() => {
 const tl = gsap.timeline({
 scrollTrigger: {
 trigger: labelRef.current,
 start:'top 80%',
 },
 });

 tl.fromTo(
 labelRef.current,
 { y: 20, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.5, ease:'power3.out' },
 )
 .fromTo(
 headRef.current?.children ?? [],
 { y: 50, opacity: 0, skewY: 2 },
 {
 y: 0,
 opacity: 1,
 skewY: 0,
 stagger: 0.15,
 duration: 0.8,
 ease:'power4.out',
 },
'-=0.2',
 )
 .fromTo(
 lineRef.current,
 { scaleX: 0 },
 {
 scaleX: 1,
 duration: 0.7,
 ease:'power3.out',
 transformOrigin:'left',
 },
'-=0.4',
 )
 .fromTo(
 subRef.current,
 { y: 20, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.6, ease:'power3.out' },
'-=0.3',
 );
 });

 return () => ctx.revert();
 }, []);

 return (
 <div className="text-center max-w-2xl mx-auto space-y-5 mb-16 lg:mb-20">

 <div ref={headRef} className="space-y-1 overflow-hidden">
 <h2
 className="font-display font-black text-slate-900 leading-tight"
 style={{ fontSize:'clamp(2.2rem, 4.5vw, 3.5rem)' }}
 >
 {data.headline}
 </h2>
 <h2
 className="font-display font-black text-gradient-primary leading-tight"
 style={{ fontSize:'clamp(2.2rem, 4.5vw, 3.5rem)' }}
 >
 {data.headlineAccent}
 </h2>
 </div>

 <div
 ref={lineRef}
 className="h-[2px] w-16 mx-auto rounded-full"
 style={{
 background:'linear-gradient(90deg, #1d4ed8, #f59e0b)',
 transform:'scaleX(0)',
 }}
 />

 <p
 ref={subRef}
 className="text-slate-600 text-[15px] leading-relaxed"
 style={{ opacity: 0 }}
 >
 {data.subheadline}
 </p>
 </div>
 );
};

/* ─────────────────────────────────────────────
 CARDS GRID — stagger reveal
───────────────────────────────────────────── */
const CardsGrid = ({ services, activeIndex, onHover }) => {
 const gridRef = useRef(null);

 useEffect(() => {
 const ctx = gsap.context(() => {
 const cards = gridRef.current?.querySelectorAll('.service-card-wrap');
 if (!cards?.length) return;

 gsap.fromTo(
 cards,
 { y: 70, opacity: 0, scale: 0.94 },
 {
 y: 0,
 opacity: 1,
 scale: 1,
 stagger: {
 each: 0.15,
 from:'start',
 },
 duration: 0.8,
 ease:'power3.out',
 scrollTrigger: {
 trigger: gridRef.current,
 start:'top 75%',
 },
 },
 );
 }, gridRef);

 return () => ctx.revert();
 }, []);

 return (
 <div
 ref={gridRef}
 className="grid grid-cols-1 sm:grid-cols-2 gap-5"
 >
 {services.map((service, i) => (
 <div
 key={service.id}
 onMouseEnter={() => onHover(i)}
 >
 <ServiceCard service={service} index={i} />
 </div>
 ))}
 </div>
 );
};

/* ─────────────────────────────────────────────
 BOTTOM CTA STRIP
───────────────────────────────────────────── */
const BottomCTA = ({ cta }) => {
 const ref = useRef(null);
 const btnRef = useMagnetic(0.3);

 useEffect(() => {
 const ctx = gsap.context(() => {
 gsap.fromTo(
 ref.current,
 { y: 30, opacity: 0 },
 {
 y: 0,
 opacity: 1,
 duration: 0.7,
 ease:'power3.out',
 scrollTrigger: {
 trigger: ref.current,
 start:'top 90%',
 },
 },
 );
 }, ref);

 return () => ctx.revert();
 }, []);

 return (
 <div
 ref={ref}
 className="mt-16 flex flex-col sm:flex-row items-center
 justify-between gap-6 rounded-2xl p-6 sm:p-8 bg-blue-50 border border-blue-200"
 style={{
 opacity: 0,
 }}
 >
 {/* Left text */}
 <div className="text-center sm:text-left space-y-1">
 <p className="text-slate-900 font-semibold text-lg">
 Need a customized solution?
 </p>
 <p className="text-slate-600 text-sm">
 Let's discuss how we can tailor our services to your specific needs.
 </p>
 </div>

 {/* Right CTA */}
 <div className="flex items-center gap-3 flex-shrink-0">
 <Link
 to={cta.href}
 className="group inline-flex items-center gap-2 text-sm
 text-slate-600 hover:text-slate-900 transition-colors duration-300"
 >
 <span>Browse All Services</span>
 <ChevronRight
 size={14}
 className="transition-transform duration-300
 group-hover:translate-x-1 text-primary-500"
 />
 </Link>

 <div className="w-px h-5 bg-slate-300" />

 <Link
 ref={btnRef}
 to="/contact"
 className="inline-flex items-center gap-2 px-6 py-3
 bg-primary-600 hover:bg-primary-500 text-white
 font-semibold rounded-xl text-sm
 transition-all duration-300 hover:shadow-glow-primary
 hover:-translate-y-0.5"
 >
 <span>Get In Touch</span>
 <MoveRight size={14} />
 </Link>
 </div>
 </div>
 );
};

/* ─────────────────────────────────────────────
 FLOATING BACKGROUND SHAPES
───────────────────────────────────────────── */
const BgShapes = () => {
 const shape1 = useRef(null);
 const shape2 = useRef(null);
 const shape3 = useRef(null);

 useEffect(() => {
 gsap.to(shape1.current, {
 y: -30, rotate: 15,
 duration: 6, ease:'sine.inOut',
 repeat: -1, yoyo: true,
 });
 gsap.to(shape2.current, {
 y: 25, rotate: -10,
 duration: 8, ease:'sine.inOut',
 repeat: -1, yoyo: true, delay: 1,
 });
 gsap.to(shape3.current, {
 y: -20, x: 10,
 duration: 7, ease:'sine.inOut',
 repeat: -1, yoyo: true, delay: 2,
 });
 }, []);

 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 {/* Shape 1 — top right */}
 <div
 ref={shape1}
 className="absolute -top-10 -right-10 w-72 h-72 rounded-full"
 style={{
 background:
'radial-gradient(circle, rgba(29,78,216,0.12) 0%, transparent 70%)',
 filter:'blur(40px)',
 }}
 />
 {/* Shape 2 — bottom left */}
 <div
 ref={shape2}
 className="absolute -bottom-20 -left-10 w-96 h-96 rounded-full"
 style={{
 background:
'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)',
 filter:'blur(60px)',
 }}
 />
 {/* Shape 3 — center */}
 <div
 ref={shape3}
 className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
 w-80 h-80 rounded-full"
 style={{
 background:
'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)',
 filter:'blur(50px)',
 }}
 />

 {/* Grid dots */}
 <div className="absolute inset-0 dot-pattern opacity-[0.07]" />
 </div>
 );
};

/* ─────────────────────────────────────────────
 VERTICAL LABEL
───────────────────────────────────────────── */
const VerticalLabel = ({ text, side ='left' }) => (
 <div
 className={`absolute ${side}-4 top-1/2 -translate-y-1/2
 hidden xl:flex flex-col items-center gap-3 pointer-events-none z-10`}
 >
 <div
 className="w-px h-16"
 style={{
 background:
 side ==='left'
 ?'linear-gradient(to bottom, transparent, rgba(29,78,216,0.4))'
 :'linear-gradient(to bottom, transparent, rgba(245,158,11,0.4))',
 }}
 />
 <p
 className="text-[10px] font-semibold tracking-[0.4em] text-slate-400
 uppercase"
 style={{ writingMode:'vertical-lr' }}
 >
 {text}
 </p>
 <div
 className="w-px h-16"
 style={{
 background:
 side ==='left'
 ?'linear-gradient(to top, transparent, rgba(29,78,216,0.4))'
 :'linear-gradient(to top, transparent, rgba(245,158,11,0.4))',
 }}
 />
 </div>
);

/* ─────────────────────────────────────────────
 MAIN SERVICES SECTION
───────────────────────────────────────────── */
const ServicesSection = () => {
 const { services } = homeData;
 const [activeIndex, setActiveIndex] = useState(0);
 const sectionRef = useRef(null);
 const bgRef = useRef(null);

 /* Parallax background */
 useEffect(() => {
 const ctx = gsap.context(() => {
 gsap.to(bgRef.current, {
 yPercent: 25,
 ease:'none',
 scrollTrigger: {
 trigger: sectionRef.current,
 start:'top bottom',
 end:'bottom top',
 scrub: 2,
 },
 });
 }, sectionRef);

 return () => ctx.revert();
 }, []);

 const handleCardHover = useCallback((index) => {
 setActiveIndex(index);
 }, []);

 const activeService = services.items[activeIndex];

 return (
 <section
 ref={sectionRef}
 id="services"
 className="relative overflow-hidden section-py bg-slate-50"
 >
 {/* ── BG ── */}
 <div ref={bgRef} className="absolute inset-0">
 <BgShapes />
 </div>

 {/* Edge fades */}
 <div
 className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-10"
 style={{
 background:
'linear-gradient(to bottom, #f8fafc 0%, transparent 100%)',
 }}
 />
 <div
 className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10"
 style={{
 background:
'linear-gradient(to top, #f8fafc 0%, transparent 100%)',
 }}
 />

 {/* Side labels */}
 <VerticalLabel text="Our Services" side="left" />
 <VerticalLabel text="03 / What We Do" side="right" />

 <div className="container-fluid relative z-20">

 {/* Section header */}
 <SectionHeader data={services} />

 {/* ── MAIN LAYOUT ── */}
 <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 xl:gap-12">

 {/* Cards grid — left (3/5) */}
 <div className="xl:col-span-3">
 <CardsGrid
 services={services.items}
 activeIndex={activeIndex}
 onHover={handleCardHover}
 />
 </div>

 {/* Detail panel — right (2/5) desktop only */}
 <div className="xl:col-span-2 hidden xl:block">
 <DetailPanel service={activeService} />
 </div>
 </div>

 {/* Bottom CTA strip */}
 <BottomCTA cta={services.cta} />
 </div>
 </section>
 );
};

export default ServicesSection;